import React from "react";
import { App as AntdApp, Button, Card, DatePicker, Form, Input, Modal, Select, Typography } from "antd";

import { loadOrgTree } from "@modules/org/api/orgApi.js";
import { createPromo } from "../api/promoApi.js";

const { Title } = Typography;

/**
 * Modal per creare una nuova Promo.
 * Cascata Consorzio -> Cooperativa (correlati come nel contesto volantino).
 */
export default function CreatePromoModal({ open, onClose, onPromoCreated }) {
	const { message } = AntdApp.useApp();
	const [form] = Form.useForm();
	const [consorzi, setConsorzi] = React.useState([]);
	const [cooperative, setCooperative] = React.useState([]);
	const [orgItems, setOrgItems] = React.useState([]);
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	React.useEffect(() => {
		(async () => {
			const items = await loadOrgTree();
			setOrgItems(items);
			setConsorzi(items.filter(x => x.type === "CONSORZIO").map(x => ({ label: x.name, value: String(x._id) })));
		})();
	}, []);

	// Con destroyOnHidden ogni apertura monta un Form fresco: non serve resetFields manuale.
	React.useEffect(() => {
		if (!open) setCooperative([]);
	}, [open]);

	const onConsorzioChange = (id) => {
		form.setFieldsValue({ cooperativaId: null });
		const coops = orgItems
			.filter(item => item.type === "COOPERATIVA" && String(item.parentId) === String(id))
			.map(item => ({ label: item.name, value: String(item._id) }));
		setCooperative(coops);
	};

	const onFilter = (input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const onSubmit = async () => {
		if (isSubmitting) return;
		setIsSubmitting(true);
		try {
			const v = await form.validateFields();
			const payload = {
				name: String(v.name || "").trim(),
				description: v.description ? String(v.description).trim() : null,
				consorzioId: v.consorzioId,
				cooperativaId: v.cooperativaId,
				sellOutFrom: v.range?.[0] ? v.range[0].format("YYYY-MM-DD") : null,
				sellOutTo: v.range?.[1] ? v.range[1].format("YYYY-MM-DD") : null,
				startDate: v.startDate ? v.startDate.format("YYYY-MM-DD") : null
			};
			const data = await createPromo(payload);
			message.success("Promo creata!");
			onPromoCreated?.(data.promo || data);
			onClose?.();
		}
		catch (error) {
			if (error?.errorFields) return;
			message.error(error?.response?.data?.error || "Errore durante la creazione della promo");
		}
		finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			width={520}
			maskClosable={false}
			className="sis-modal"
			title="Nuova Promo"
			destroyOnHidden
		>
			<Card className="card-elev">
				<Title level={5} style={{ marginBottom: 16 }}>Dati Promo</Title>
				<Form form={form} layout="vertical" onFinish={onSubmit}>
					<Form.Item label="Consorzio" name="consorzioId" rules={[{ required: true, message: "Seleziona un consorzio" }]}>
						<Select placeholder="Seleziona consorzio" showSearch options={consorzi} onChange={onConsorzioChange} allowClear filterOption={onFilter} />
					</Form.Item>
					<Form.Item label="Cooperativa" name="cooperativaId" rules={[{ required: true, message: "Seleziona una cooperativa" }]}>
						<Select disabled={!cooperative.length} placeholder="Seleziona cooperativa" options={cooperative} showSearch allowClear filterOption={onFilter} />
					</Form.Item>
					<Form.Item label="Nome" name="name" rules={[{ required: true, whitespace: true, message: "Inserisci il nome della promo" }]}>
						<Input placeholder="Es. Promo Primavera 2026" />
					</Form.Item>
					<Form.Item label="Descrizione" name="description">
						<Input.TextArea rows={2} placeholder="Descrizione (opzionale)" />
					</Form.Item>
					<Form.Item label="Sellout" name="range" rules={[{ required: true, message: "Seleziona l'intervallo Sellout" }]}>
						<DatePicker.RangePicker format="DD/MM/YYYY" placeholder={["Dal", "Al"]} style={{ width: "100%" }} />
					</Form.Item>
					<Form.Item label="Data Avvio" name="startDate" rules={[{ required: true, message: "Seleziona la data di avvio" }]}>
						<DatePicker format="DD/MM/YYYY" placeholder="Data avvio" style={{ width: "100%" }} />
					</Form.Item>
					<Button type="primary" htmlType="submit" size="large" block loading={isSubmitting} disabled={isSubmitting}>
						Crea Promo
					</Button>
				</Form>
			</Card>
		</Modal>
	);
}
